import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { ApiError } from "./api-error.ts";

export function shouldRetry(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError) {
    // All 4xx are non-retryable at the query layer. A 429 gets ONE transport-
    // level retry from the API client interceptor (respects Retry-After), and
    // only when the request is repeatable: a GET/HEAD carrying no single-use
    // Turnstile challenge. Every other rate-limited request — every mutation
    // among them — surfaces its 429 straight to the caller.
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 2; // 5xx/network: 2 retries
}

export function getRetryDelay(failureCount: number): number {
  return Math.min(1000 * 2 ** failureCount, 30000);
}

export function createQueryClient(options?: {
  onError?: (error: Error) => void;
  /**
   * Per-app override for the query (read-path) retry predicate. Defaults to
   * shouldRetry (4xx non-retryable, 5xx/network retried twice). The customer
   * app passes a no-retry predicate to break the metastable retry storm under
   * its org-less RLS read load. Mutations never retry (see below).
   */
  queryRetry?: (failureCount: number, error: Error) => boolean;
}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: options?.onError,
    }),
    mutationCache: new MutationCache({
      onError: options?.onError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
        retry: options?.queryRetry ?? shouldRetry,
        retryDelay: getRetryDelay,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Mutations NEVER auto-retry: most writes are not idempotent (e.g.
        // POST /tasks carries no client id), and a first attempt can commit
        // server-side while failing client-side (connection drop under load).
        // A blind retry then duplicates the row — observed live as a
        // double-created subtask. Failures surface to the user, who retries
        // deliberately.
        retry: false,
      },
    },
  });
}
